import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Download, Search, MessageCircle, ArrowLeft, AlertTriangle, BookOpen, Send,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PaginationBar } from "@/components/PaginationBar";

const MODULES = [
  { key: "all",       label: "All",      hint: "hostel + mess combined" },
  { key: "residence", label: "Hostel",   hint: "residence dues only" },
  { key: "mess",      label: "Mess",     hint: "mess dues only" },
];

const MODULE_QUERY = { all: "", residence: "residence", mess: "mess" };

function balanceFor(row, module) {
  if (module === "residence") return row.residence_balance || 0;
  if (module === "mess") return row.mess_balance || 0;
  return row.balance || 0;
}

function buildReminder(row, module) {
  const bal = Number(balanceFor(row, module)).toLocaleString("en-IN");
  const label = module === "residence" ? "hostel" : module === "mess" ? "mess" : "hostel & mess";
  const expiryLine = row.expiry_date ? `\nSubscription end date: ${row.expiry_date}` : "";
  return `Dear ${row.name},

This is a friendly reminder that your ${label} account has an outstanding balance of ₹${bal} at Param Social Foundation.${expiryLine}

Please clear the dues at your earliest convenience.

— Param Social Foundation`;
}

export default function OutstandingDues() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [module, setModule] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const canSee = ["admin", "accountant", "warden", "hostel_manager"].includes(user?.role);

  const load = () => {
    setLoading(true);
    const qs = MODULE_QUERY[module] ? `?module=${MODULE_QUERY[module]}` : "";
    api.get(`/reports/outstanding${qs}`)
      .then((r) => setRows(r.data || []))
      .catch((e) => { toast.error(formatApiError(e)); setRows([]); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (canSee) load(); /* eslint-disable-next-line */ }, [module]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.trim().toLowerCase();
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.phone || "").toLowerCase().includes(s) ||
      (r.room_no || "").toLowerCase().includes(s) ||
      (r.company || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const totals = useMemo(() => {
    const t = { count: filtered.length, hostel: 0, mess: 0, expired: 0, balance: 0 };
    for (const r of filtered) {
      t.balance += Number(r.balance || 0);
      t.hostel  += Number(r.residence_balance || 0);
      t.mess    += Number(r.mess_balance || 0);
      t.expired += Number(r.expired_balance || 0);
    }
    return t;
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / limit));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * limit, page * limit),
    [filtered, page, limit]
  );
  useEffect(() => { setPage(1); }, [q, module]);

  const nudge = (row) => {
    const phone = (row.phone || "").replace(/\D/g, "");
    if (!phone) { toast.error(`No mobile number on file for ${row.name}`); return; }
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(buildReminder(row, module))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const bulkNudge = () => {
    const withPhone = filtered.filter((r) => (r.phone || "").replace(/\D/g, ""));
    if (withPhone.length === 0) { toast.error("None of the filtered rows have a mobile on file"); return; }
    if (!window.confirm(`Open WhatsApp for ${withPhone.length} resident(s)? Each will open in a new tab.`)) return;
    withPhone.forEach((r, i) => {
      // stagger to reduce browser blocking
      setTimeout(() => {
        const phone = r.phone.replace(/\D/g, "");
        window.open(
          `https://wa.me/${phone}?text=${encodeURIComponent(buildReminder(r, module))}`,
          "_blank", "noopener,noreferrer",
        );
      }, i * 250);
    });
    toast.success(`Opened ${withPhone.length} WhatsApp chat(s)`);
  };

  const exportCsv = () => {
    const header = ["Name", "Mobile", "Room", "Hostel Balance", "Mess Balance", "Expired Balance", "Total Balance", "Expiry Date"];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(",")];
    for (const r of filtered) {
      lines.push([
        r.name, r.phone || "",
        [r.building, r.floor, r.room_no, r.bed_no].filter(Boolean).join("/") || "",
        r.residence_balance || 0, r.mess_balance || 0,
        r.expired_balance || 0, r.balance || 0,
        r.expiry_date || "",
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `outstanding-dues-${module}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (!canSee) {
    return (
      <div className="p-8 text-center text-muted-foreground" data-testid="outstanding-forbidden">
        You don't have access to this report.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="outstanding-dues-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Finance</div>
          <h1 className="font-display text-4xl font-bold mt-1">Outstanding Dues</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            Every resident with a pending balance. Filter by module, search the roster,
            or open a WhatsApp reminder in one click.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={bulkNudge} data-testid="bulk-nudge" disabled={!filtered.length}>
            <Send className="w-4 h-4 mr-2" /> Nudge all ({filtered.length})
          </Button>
        </div>
      </div>

      {/* MODULE TABS */}
      <div className="flex gap-2" data-testid="module-tabs">
        {MODULES.map((m) => (
          <button
            key={m.key}
            onClick={() => setModule(m.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-all ${
              module === m.key
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-white border-border hover:border-primary/40"
            }`}
            data-testid={`module-${m.key}`}
          >
            {m.label}
            <span className="ml-2 text-xs opacity-70">· {m.hint}</span>
          </button>
        ))}
      </div>

      {/* SUMMARY TILES */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile label="Residents with dues" value={totals.count}                         tone="red"   tid="tot-count" />
        <SummaryTile label="Hostel outstanding"   value={`₹${totals.hostel.toLocaleString("en-IN")}`}  tone="orange" tid="tot-hostel" />
        <SummaryTile label="Mess outstanding"     value={`₹${totals.mess.toLocaleString("en-IN")}`}    tone="yellow" tid="tot-mess" />
        <SummaryTile label="Expired subs balance" value={`₹${totals.expired.toLocaleString("en-IN")}`} tone="red"    tid="tot-expired" />
      </div>

      {/* SEARCH */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by name, mobile, ID, room…"
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" />
        </div>
        <div className="text-sm text-muted-foreground">Total dues: <span className="font-mono font-semibold text-foreground">₹{totals.balance.toLocaleString("en-IN")}</span></div>
      </div>

      <Card className="p-4 border-border bg-white">
        {loading && <div className="py-10 text-center text-muted-foreground">Loading…</div>}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="outstanding-table">
              <thead>
                <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                  <th className="py-3 pr-3">Resident</th>
                  <th className="py-3 pr-3">Room</th>
                  <th className="py-3 pr-3">Mobile</th>
                  <th className="py-3 pr-3 text-right">Hostel Bal.</th>
                  <th className="py-3 pr-3 text-right">Mess Bal.</th>
                  <th className="py-3 pr-3 text-right">Expired</th>
                  <th className="py-3 pr-3 text-right">Total</th>
                  <th className="py-3 pr-3">Pending Mo.</th>
                  <th className="py-3 pr-3">Expiry</th>
                  <th className="py-3 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr key={r.resident_id} className="border-b border-border/40 hover:bg-secondary/40"
                      data-testid={`row-${r.resident_id}`}>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        {r.company || "—"}
                      </div>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {[r.building, r.floor, r.room_no, r.bed_no].filter(Boolean).join("/") || "—"}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.phone || "—"}</td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {r.residence_balance > 0
                        ? <span className="text-destructive">₹{Number(r.residence_balance).toLocaleString("en-IN")}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">
                      {r.mess_balance > 0
                        ? <span className="text-destructive">₹{Number(r.mess_balance).toLocaleString("en-IN")}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {r.expired_balance > 0
                        ? <Badge variant="destructive" className="font-mono text-[10px]">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            ₹{Number(r.expired_balance).toLocaleString("en-IN")}
                          </Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono font-bold">
                      ₹{Number(r.balance).toLocaleString("en-IN")}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {r.pending_months > 0 ? (
                        <span className="inline-flex items-center gap-1">
                          <Badge variant="destructive" className="font-mono text-[10px]">
                            {r.pending_months} mo.
                          </Badge>
                          {r.monthly_hostel > 0 && (
                            <span className="text-muted-foreground">
                              ₹{Number(r.monthly_hostel).toLocaleString("en-IN")}/mo
                            </span>
                          )}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.expiry_date || "—"}</td>
                    <td className="py-2 pr-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline"
                          onClick={() => navigate(`/residents/${r.resident_id}/ledger`)}
                          data-testid={`ledger-${r.resident_id}`} title="Open ledger">
                          <BookOpen className="w-3 h-3" />
                        </Button>
                        <Button size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => nudge(r)}
                          data-testid={`nudge-${r.resident_id}`} title="WhatsApp nudge"
                          disabled={!r.phone}>
                          <MessageCircle className="w-3 h-3 mr-1" /> Nudge
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="py-10 text-center text-muted-foreground">
                      {rows.length === 0
                        ? "No outstanding dues. All clear ✨"
                        : "No matches for your search."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <PaginationBar page={page} total={filtered.length} limit={limit} totalPages={totalPages}
          onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
      </Card>
    </div>
  );
}

const TONE = { red: "bg-destructive", orange: "bg-orange-500", yellow: "bg-yellow-500", green: "bg-emerald-500" };
const SummaryTile = ({ label, value, tone, tid }) => (
  <Card className="p-3 border-border bg-white flex items-center gap-3" data-testid={tid}>
    <span className={`w-2 h-10 rounded-full ${TONE[tone]}`} />
    <div>
      <div className="eyebrow text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-xl mt-1">{value}</div>
    </div>
  </Card>
);
